import { useEffect, useState } from 'react';
import { create } from 'zustand';
import { api } from './api';
import { isLocalOnly } from './local';
import { useSession } from './session';

/** Card machines (pin pads / EDC) of this store. The server talks to the provider; keys never reach the till. */
export interface CardField { key: string; label: string; secret?: boolean; hint?: string; options?: string[]; optional?: boolean }
export interface CardProvider { id: string; label: string; fields: CardField[] }
export interface CardTerminal { id: string; provider: string; providerLabel: string; label: string; deviceId: string | null; config: Record<string, string>; hasKeys: boolean }
export type ChargeMode = 'card' | 'upi' | 'any';
export interface Charge { id: string; status: 'pending' | 'approved' | 'declined' | 'cancelled' | 'failed'; amount: number; mode: ChargeMode; message?: string; card?: string; authCode?: string; rrn?: string; method?: string }

const KEY = 'sd.cardTerminals';
const cached = (): CardTerminal[] => { try { return JSON.parse(localStorage.getItem(KEY) ?? '[]'); } catch { return []; } };

export const useCardTerminals = create<{ list: CardTerminal[]; loaded: boolean }>(() => ({ list: cached(), loaded: false }));

export async function loadCardTerminals() {
  if (isLocalOnly()) return []; // card machines are linked through the server
  const r = await api<{ terminals: CardTerminal[] }>('/api/card/terminals');
  try { localStorage.setItem(KEY, JSON.stringify(r.terminals)); } catch { /* storage blocked */ }
  useCardTerminals.setState({ list: r.terminals, loaded: true });
  return r.terminals;
}

/** The machine this till uses: one linked to this till, else a store machine not tied to any till. */
export function useTillTerminal(): CardTerminal | null {
  const list = useCardTerminals((s) => s.list);
  const did = useSession((s) => s.me?.device.id);
  useEffect(() => { void loadCardTerminals().catch(() => {}); }, []);
  return list.find((t) => t.deviceId === did) ?? list.find((t) => !t.deviceId) ?? null;
}

export function useCardProviders() {
  const [providers, setProviders] = useState<CardProvider[]>([]);
  useEffect(() => { void api<{ providers: CardProvider[] }>('/api/card/providers').then((r) => setProviders(r.providers), () => {}); }, []);
  return providers;
}

/** Short text kept on the bill's payment line (max 40 characters). */
export const chargeRef = (c: Charge) => [c.method, c.card, c.authCode && `AUTH ${c.authCode}`, !c.authCode && c.rrn && `RRN ${c.rrn}`].filter(Boolean).join(' ').slice(0, 40) || 'card machine';

/**
 * Send the amount and mode to the machine, then follow it until the customer has paid, it was declined or
 * cancelled. `onUpdate` gets every state. Returns the final charge.
 */
export async function chargeOnMachine(terminalId: string, amount: number, mode: ChargeMode, billRef: string, onUpdate: (c: Charge) => void, stop: { cancelled: boolean }): Promise<Charge> {
  const id = crypto.randomUUID();
  let c = (await api<{ charge: Charge }>('/api/card/charges', { method: 'POST', json: { id, terminalId, amount, mode, billRef } })).charge;
  onUpdate(c);
  let misses = 0;
  while (c.status === 'pending') {
    await new Promise((r) => setTimeout(r, 2000));
    if (stop.cancelled) {
      c = (await api<{ charge: Charge }>(`/api/card/charges/${id}/cancel`, { method: 'POST', json: {} })).charge;
      onUpdate(c);
      if (c.status === 'pending') { stop.cancelled = false; continue; } // e.g. Paytm: cancel on the machine itself
      break;
    }
    try {
      c = (await api<{ charge: Charge }>(`/api/card/charges/${id}`)).charge;
      misses = 0;
    } catch (e) {
      // A brief network drop must not lose a payment the customer may be making: keep asking.
      if (++misses > 30) throw e;
      continue;
    }
    onUpdate(c);
  }
  return c;
}
